import {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import type {PartStorageLocation, PartStorageSubLocation, PartStorageSegment} from '../core/_models'
import {
  getPartStorageLocations,
  createPartStorageLocation,
  updatePartStorageLocation,
  deletePartStorageLocation,
  createPartStorageSubLocation,
  updatePartStorageSubLocation,
  deletePartStorageSubLocation,
  createPartStorageSegment,
  updatePartStorageSegment,
  deletePartStorageSegment,
} from '../core/_requests'

interface PartStorageLocationLibraryModalProps {
  visible: boolean
  onClose: () => void
  canCreate?: boolean
  canUpdate?: boolean
  canDelete?: boolean
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const PartStorageLocationLibraryModal: FC<PartStorageLocationLibraryModalProps> = ({
  visible,
  onClose,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}) => {
  const [locations, setLocations] = useState<PartStorageLocation[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null)
  const [selectedSubLocationId, setSelectedSubLocationId] = useState<number | null>(null)
  const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(null)

  const [locationName, setLocationName] = useState('')
  const [subLocationName, setSubLocationName] = useState('')
  const [segmentName, setSegmentName] = useState('')
  const [subLocationOptional, setSubLocationOptional] = useState(false)
  const [segmentOptional, setSegmentOptional] = useState(false)
  const [expandedLocations, setExpandedLocations] = useState<Set<number>>(new Set())
  const [expandedSubLocations, setExpandedSubLocations] = useState<Set<number>>(new Set())
  const canSaveLocation = selectedLocationId ? canUpdate : canCreate
  const canSaveSubLocation = selectedSubLocationId ? canUpdate : canCreate
  const canSaveSegment = selectedSegmentId ? canUpdate : canCreate

  const loadLocations = async () => {
    setIsLoading(true)
    try {
      const data = await getPartStorageLocations()
      setLocations(Array.isArray(data) ? data : [])
      setSelectedLocationId((current) =>
        current && Array.isArray(data) && data.some((location) => location.id === current)
          ? current
          : null
      )
      setSelectedSubLocationId((current) =>
        current &&
        Array.isArray(data) &&
        data.some((location) =>
          (location.subLocations || []).some((subLocation) => subLocation.id === current)
        )
          ? current
          : null
      )
      setSelectedSegmentId((current) =>
        current &&
        Array.isArray(data) &&
        data.some((location) =>
          (location.subLocations || []).some((subLocation) =>
            (subLocation.segments || []).some((segment) => segment.id === current)
          )
        )
          ? current
          : null
      )
    } catch (error: any) {
      console.error('Error loading storage locations:', error)
      toast.error('Failed to load storage locations')
      setLocations([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (visible) {
      loadLocations()
    }
  }, [visible])

  useEffect(() => {
    if (!visible) {
      setSelectedLocationId(null)
      setSelectedSubLocationId(null)
      setSelectedSegmentId(null)
      setLocationName('')
      setSubLocationName('')
      setSegmentName('')
      setSubLocationOptional(false)
      setSegmentOptional(false)
      setExpandedLocations(new Set())
      setExpandedSubLocations(new Set())
    }
  }, [visible])

  const selectedLocation = useMemo(
    () => locations.find((loc) => loc.id === selectedLocationId) || null,
    [locations, selectedLocationId]
  )

  const subLocations = selectedLocation?.subLocations || []

  const selectedSubLocation = useMemo(
    () => subLocations.find((sub) => sub.id === selectedSubLocationId) || null,
    [subLocations, selectedSubLocationId]
  )

  const segments = selectedSubLocation?.segments || []

  useEffect(() => {
    setSelectedSubLocationId(null)
    setSelectedSegmentId(null)
    setSubLocationName('')
    setSegmentName('')
    setSubLocationOptional(false)
    setSegmentOptional(false)
    if (selectedLocationId) {
      setExpandedLocations((prev) => {
        const next = new Set(prev)
        next.add(selectedLocationId)
        return next
      })
    }
  }, [selectedLocationId, locations])

  useEffect(() => {
    setSelectedSegmentId(null)
    setSegmentName('')
    setSegmentOptional(false)
    if (selectedSubLocationId) {
      setExpandedSubLocations((prev) => {
        const next = new Set(prev)
        next.add(selectedSubLocationId)
        return next
      })
    }
  }, [selectedSubLocationId])

  const toggleLocationExpansion = (locationId: number) => {
    setExpandedLocations((prev) => {
      const next = new Set(prev)
      if (next.has(locationId)) {
        next.delete(locationId)
      } else {
        next.add(locationId)
      }
      return next
    })
  }

  const toggleSubLocationExpansion = (subLocationId: number) => {
    setExpandedSubLocations((prev) => {
      const next = new Set(prev)
      if (next.has(subLocationId)) {
        next.delete(subLocationId)
      } else {
        next.add(subLocationId)
      }
      return next
    })
  }

  const handleSelectLocation = (loc: PartStorageLocation) => {
    setSelectedLocationId(loc.id)
    setLocationName(loc.name)
  }

  const handleSelectSubLocation = (sub: PartStorageSubLocation) => {
    setSelectedSubLocationId(sub.id)
    setSubLocationName(sub.name)
    setSubLocationOptional(Boolean(sub.optional))
  }

  const handleSelectSegment = (segment: PartStorageSegment) => {
    setSelectedSegmentId(segment.id)
    setSegmentName(segment.name)
    setSegmentOptional(Boolean(segment.optional))
  }

  const handleSaveLocation = async () => {
    const name = locationName.trim()
    if (!name) {
      toast.error('Storage location name is required')
      return
    }
    try {
      if (selectedLocationId) {
        await updatePartStorageLocation(selectedLocationId, name)
        toast.success('Storage location updated')
        setLocationName('')
        await loadLocations()
      } else {
        const created = await createPartStorageLocation(name)
        toast.success('Storage location created')
        setLocationName('')
        setSelectedLocationId(created?.id ?? null)
        await loadLocations()
      }
    } catch (error: any) {
      toast.error('Failed to save storage location')
    }
  }

  const handleDeleteLocation = async () => {
    if (!selectedLocationId) return
    try {
      await deletePartStorageLocation(selectedLocationId)
      toast.success('Storage location deleted')
      setSelectedLocationId(null)
      setLocationName('')
      await loadLocations()
    } catch (error: any) {
      toast.error('Unable to delete storage location')
    }
  }

  const handleSaveSubLocation = async () => {
    if (!selectedLocationId) {
      toast.error('Select a storage location first')
      return
    }
    const name = subLocationName.trim()
    if (!name) {
      toast.error('Sub storage location name is required')
      return
    }
    try {
      if (selectedSubLocationId) {
        await updatePartStorageSubLocation(selectedSubLocationId, name, subLocationOptional)
        toast.success('Sub storage location updated')
      } else {
        await createPartStorageSubLocation(selectedLocationId, name, subLocationOptional)
        toast.success('Sub storage location created')
      }
      setSubLocationName('')
      setSubLocationOptional(false)
      setSelectedSubLocationId(null)
      await loadLocations()
    } catch (error: any) {
      toast.error('Failed to save sub storage location')
    }
  }

  const handleDeleteSubLocation = async () => {
    if (!selectedSubLocationId) return
    try {
      await deletePartStorageSubLocation(selectedSubLocationId)
      toast.success('Sub storage location deleted')
      setSelectedSubLocationId(null)
      setSubLocationName('')
      await loadLocations()
    } catch (error: any) {
      toast.error('Unable to delete sub storage location')
    }
  }

  const handleSaveSegment = async () => {
    if (!selectedSubLocationId) {
      toast.error('Select a sub storage location first')
      return
    }
    const name = segmentName.trim()
    if (!name) {
      toast.error('Segment name is required')
      return
    }
    try {
      if (selectedSegmentId) {
        await updatePartStorageSegment(selectedSegmentId, name, segmentOptional)
        toast.success('Segment updated')
      } else {
        await createPartStorageSegment(selectedSubLocationId, name, segmentOptional)
        toast.success('Segment created')
      }
      setSegmentName('')
      setSegmentOptional(false)
      setSelectedSegmentId(null)
      await loadLocations()
    } catch (error: any) {
      toast.error('Failed to save segment')
    }
  }

  const handleDeleteSegment = async () => {
    if (!selectedSegmentId) return
    try {
      await deletePartStorageSegment(selectedSegmentId)
      toast.success('Segment deleted')
      setSelectedSegmentId(null)
      setSegmentName('')
      await loadLocations()
    } catch (error: any) {
      toast.error('Unable to delete segment')
    }
  }

  const resetLocationForm = () => {
    setSelectedLocationId(null)
    setLocationName('')
  }

  const resetSubLocationForm = () => {
    setSelectedSubLocationId(null)
    setSubLocationName('')
    setSubLocationOptional(false)
  }

  const resetSegmentForm = () => {
    setSelectedSegmentId(null)
    setSegmentName('')
    setSegmentOptional(false)
  }

  if (!visible) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '80vw',
          height: '75vh',
          maxWidth: '1500px',
          maxHeight: '900px',
        }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-6'>Spare Storage Location Library</h5>
          <button type='button' className='btn-close m-6' onClick={onClose}></button>
        </div>
        <div className='flex-grow-1 overflow-auto px-4 py-3 m-6'>
          {isLoading && <div className='text-muted mb-3'>Loading...</div>}
          <div className='row g-4'>
              <div className='col-lg-7'>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                  <h6 className='fw-semibold mb-0'>Storage Tree</h6>
                  <span className='text-muted fs-7'>Select any node to edit on the right</span>
                </div>
                <div className='border rounded p-3 bg-light-subtle' style={{minHeight: '420px'}}>
                  {locations.length === 0 && <div className='text-muted small'>No locations yet</div>}
                  {locations.map((loc) => {
                    const isExpanded = expandedLocations.has(loc.id)
                    return (
                      <div key={loc.id} className='mb-3'>
                        <div
                          className={`d-flex align-items-center justify-content-between p-2 rounded ${
                            selectedLocationId === loc.id ? 'bg-light-primary' : ''
                          }`}
                          style={{
                            backgroundColor:
                              selectedLocationId === loc.id
                                ? 'rgba(0, 123, 255, 0.12)'
                                : 'rgba(0, 123, 255, 0.06)',
                          }}
                        >
                          <div className='d-flex align-items-center gap-2'>
                            <button
                              type='button'
                              className='btn btn-icon btn-sm btn-light'
                              onClick={() => toggleLocationExpansion(loc.id)}
                            >
                              <span className='fw-bold'>{isExpanded ? '−' : '+'}</span>
                            </button>
                            <button
                              type='button'
                              className='btn btn-link p-0 text-start'
                              onClick={() => handleSelectLocation(loc)}
                            >
                              <span className='fw-semibold text-dark'>{loc.name}</span>
                            </button>
                            <span className='badge badge-light-primary'>{loc.subLocations?.length || 0}</span>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className='ms-4 mt-2'>
                            {loc.subLocations && loc.subLocations.length > 0 ? (
                              loc.subLocations.map((sub) => {
                                const isSubExpanded = expandedSubLocations.has(sub.id)
                                return (
                                  <div key={sub.id} className='mb-2'>
                                    <div
                                      className={`d-flex align-items-center justify-content-between p-2 rounded ${
                                        selectedSubLocationId === sub.id ? 'bg-light-info' : ''
                                      }`}
                                      style={{
                                        backgroundColor:
                                          selectedSubLocationId === sub.id
                                            ? 'rgba(13, 202, 240, 0.18)'
                                            : 'rgba(13, 202, 240, 0.08)',
                                      }}
                                    >
                                      <div className='d-flex align-items-center gap-2'>
                                        <button
                                          type='button'
                                          className='btn btn-icon btn-sm btn-light'
                                          onClick={() => toggleSubLocationExpansion(sub.id)}
                                        >
                                          <span className='fw-bold'>{isSubExpanded ? '−' : '+'}</span>
                                        </button>
                                        <button
                                          type='button'
                                          className='btn btn-link p-0 text-start'
                                          onClick={() => handleSelectSubLocation(sub)}
                                        >
                                          <span className='fw-semibold text-dark'>{sub.name}</span>
                                        </button>
                                        {sub.optional && (
                                          <span className='badge badge-light-warning'>Optional</span>
                                        )}
                                      </div>
                                      <span className='badge badge-light-info'>{sub.segments?.length || 0}</span>
                                    </div>
                                    {isSubExpanded && (
                                      <div className='ms-4 mt-2'>
                                        {sub.segments && sub.segments.length > 0 ? (
                                          sub.segments.map((segment) => (
                                            <button
                                              key={segment.id}
                                              type='button'
                                              className={`btn w-100 text-start mb-2 ${
                                                selectedSegmentId === segment.id ? 'btn-light-primary' : 'btn-light'
                                              }`}
                                              onClick={() => handleSelectSegment(segment)}
                                              style={{
                                                backgroundColor:
                                                  selectedSegmentId === segment.id
                                                    ? 'rgba(255, 193, 7, 0.25)'
                                                    : 'rgba(255, 193, 7, 0.12)',
                                              }}
                                            >
                                              <span className='fw-semibold'>{segment.name}</span>
                                              {segment.optional && (
                                                <span className='badge badge-light-warning ms-2'>Optional</span>
                                              )}
                                            </button>
                                          ))
                                        ) : (
                                          <div className='text-muted small'>No segments</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )
                              })
                            ) : (
                              <div className='text-muted small'>No sub locations</div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className='col-lg-5'>
                <div className='card mb-4'>
                  <div className='card-body'>
                    <h6 className='fw-semibold mb-3'>Storage Location</h6>
                    <input
                      className='form-control mb-2'
                      placeholder='Storage location name'
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                      disabled={!canSaveLocation}
                    />
                    <div className='d-flex gap-2'>
                      {canSaveLocation && (
                        <button type='button' className='btn btn_primary btn-sm' onClick={handleSaveLocation}>
                          {selectedLocationId ? 'Update' : 'Add'}
                        </button>
                      )}
                      {selectedLocationId && canSaveLocation && (
                        <button type='button' className='btn btn-light btn-sm' onClick={resetLocationForm}>
                          Cancel
                        </button>
                      )}
                      {selectedLocationId && canDelete && (
                        <button type='button' className='btn btn-light-danger btn-sm ms-auto' onClick={handleDeleteLocation}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className='card mb-4'>
                  <div className='card-body'>
                    <h6 className='fw-semibold mb-3'>Sub Storage Location</h6>
                    <input
                      className='form-control mb-2'
                      placeholder='Sub location name'
                      value={subLocationName}
                      onChange={(e) => setSubLocationName(e.target.value)}
                      disabled={!selectedLocation || !canSaveSubLocation}
                    />
                    <div className='form-check mb-3'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='subLocationOptionalToggle'
                        checked={subLocationOptional}
                        onChange={(e) => setSubLocationOptional(e.target.checked)}
                        disabled={!selectedLocation || !canSaveSubLocation}
                      />
                      <label className='form-check-label' htmlFor='subLocationOptionalToggle'>
                        Optional sub storage
                      </label>
                    </div>
                    <div className='d-flex gap-2'>
                      {canSaveSubLocation && (
                        <button
                          type='button'
                          className='btn btn_primary btn-sm'
                          onClick={handleSaveSubLocation}
                          disabled={!selectedLocation}
                        >
                          {selectedSubLocationId ? 'Update' : 'Add'}
                        </button>
                      )}
                      {selectedSubLocationId && canSaveSubLocation && (
                        <button type='button' className='btn btn-light btn-sm' onClick={resetSubLocationForm}>
                          Cancel
                        </button>
                      )}
                      {selectedSubLocationId && canDelete && (
                        <button type='button' className='btn btn-light-danger btn-sm ms-auto' onClick={handleDeleteSubLocation}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className='card'>
                  <div className='card-body'>
                    <h6 className='fw-semibold mb-3'>Segments</h6>
                    <input
                      className='form-control mb-2'
                      placeholder='Segment name'
                      value={segmentName}
                      onChange={(e) => setSegmentName(e.target.value)}
                      disabled={!selectedSubLocation || !canSaveSegment}
                    />
                    <div className='form-check mb-3'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='segmentOptionalToggle'
                        checked={segmentOptional}
                        onChange={(e) => setSegmentOptional(e.target.checked)}
                        disabled={!selectedSubLocation || !canSaveSegment}
                      />
                      <label className='form-check-label' htmlFor='segmentOptionalToggle'>
                        Optional segment
                      </label>
                    </div>
                    <div className='d-flex gap-2'>
                      {canSaveSegment && (
                        <button
                          type='button'
                          className='btn btn_primary btn-sm'
                          onClick={handleSaveSegment}
                          disabled={!selectedSubLocation}
                        >
                          {selectedSegmentId ? 'Update' : 'Add'}
                        </button>
                      )}
                      {selectedSegmentId && canSaveSegment && (
                        <button type='button' className='btn btn-light btn-sm' onClick={resetSegmentForm}>
                          Cancel
                        </button>
                      )}
                      {selectedSegmentId && canDelete && (
                        <button type='button' className='btn btn-light-danger btn-sm ms-auto' onClick={handleDeleteSegment}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PartStorageLocationLibraryModal
